'use client';

import type { FC } from 'react';
import { RecommendationContext } from '@/configs/recommendations';
import { usePathname } from 'next/navigation';
import {
  type InteractionDetails,
  EventType
} from '@/services/isomorphic/UserInteractionService';
import { PageType } from '@/services/models/Page';
import { UserInteraction } from '@/react/components/core-ui/UserInteraction';
import { RecommendationsCarousel } from '@/react/components/product-list/recommendation/RecommendationsCarousel';
import { PageError as ErrorComponent, ErrorPageType } from '../../PageError';
import S from './styles.base.module.scss';
import { NotFoundContent } from './NotFoundContent';

/**
 * This is the template for the Not Found page.
 *
 * It encapsulates the `404` behavior to be consistent across the application.
 *
 * This is designed to be used within the Next.js app router. If you need to
 * implement on pages router, consider deprecated `@/react/components/templates/404Page` instead,
 * although we should remove it in the future.
 */
const NotFoundPage: FC = () => {
  const pathname = usePathname();
  const interaction: InteractionDetails = {
    action: EventType.PageView,
    page: {
      pageType: PageType.NotFound,
      // In order to dispatch an accurate page view event,
      // we need to provide the current URL.
      //
      // Since `not-found.tsx` doesn't access any of the URL parameters, having it
      // here client-side is enough to provide the correct URL that failed to load.
      url: pathname ?? '/not-found'
    }
  };

  return (
    <>
      <UserInteraction interaction={interaction} />
      <ErrorComponent pageType={ErrorPageType.NotFound} />
      <NotFoundContent />
      <div className={S.products}>
        <RecommendationsCarousel context={RecommendationContext.NotFoundPage} />
      </div>
    </>
  );
};

export default NotFoundPage;
